import React, { useState } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { Task, User } from '../../types';
import {
  Clock,
  Calendar,
  Flag,
  MoreHorizontal,
  Plus
} from 'lucide-react';
import { format } from 'date-fns';

interface KanbanBoardProps {
  tasks: Task[];
  users: User[];
  onTaskUpdate: (taskId: number, updates: Partial<Task>) => Promise<void>;
  onSelectTask?: (task: Task) => void;
  onCreateTask?: () => void;
  compactMode?: boolean;
}

interface Column {
  id: string;
  title: string;
  status: string;
  color: string;
  tasks: Task[];
}

// Draggable Task Card Component
const TaskCard: React.FC<{
  task: Task;
  users: User[];
  onSelect?: (task: Task) => void;
  compactMode?: boolean;
}> = ({ task, users, onSelect, compactMode }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Use assignedUser from task data (populated by backend)
  const assignedUser = task.assignedUser || users.find(u => u.id === task.assignedTo);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`bg-white rounded-lg border border-gray-200 ${compactMode ? 'p-2 mb-1.5' : 'p-3 mb-2'} shadow-sm hover:shadow-md transition-all cursor-pointer ${
        isDragging ? 'rotate-3 scale-105' : ''
      }`}
      onClick={() => onSelect?.(task)}
    >
      {/* Priority Badge */}
      <div className={`flex items-start justify-between ${compactMode ? 'mb-1' : 'mb-1.5'}`}>
        <span className={`inline-flex px-1.5 py-0.5 text-xs font-medium rounded-full border ${getPriorityColor(task.priority)}`}>
          <Flag className="w-3 h-3 mr-1" />
          {task.priority}
        </span>
        <button className="text-gray-400 hover:text-gray-600 p-1">
          <MoreHorizontal className="w-4 h-4" />
        </button>
      </div>

      {/* Task Title */}
      <h3 className={`font-medium text-gray-900 ${compactMode ? 'mb-1 text-sm' : 'mb-1.5'} line-clamp-2`}>
        {task.title || task.name}
      </h3>

      {/* Task Description */}
      {task.description && !compactMode && (
        <p className="text-sm text-gray-600 mb-2 line-clamp-2">
          {task.description}
        </p>
      )}

      {/* Task Details */}
      <div className="flex items-center justify-between text-xs text-gray-500">
        <div className="flex items-center space-x-3">
          {/* Due Date */}
          {task.dueDate && (
            <div className="flex items-center">
              <Calendar className="w-3 h-3 mr-1" />
              {format(new Date(task.dueDate), 'MMM dd')}
            </div>
          )}

          {/* Estimated Hours */}
          {task.estimatedHours && (
            <div className="flex items-center">
              <Clock className="w-3 h-3 mr-1" />
              {task.estimatedHours}h
            </div>
          )}
        </div>

        {/* Assigned User - Initials Badge */}
        <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
          <span className="text-xs font-medium text-indigo-700">
            {assignedUser ? `${assignedUser.first_name?.[0] || ''}${assignedUser.last_name?.[0] || ''}` : '?'}
          </span>
        </div>
      </div>
    </div>
  );
};

// Droppable Column Component
const KanbanColumn: React.FC<{
  column: Column;
  users: User[];
  onSelectTask?: (task: Task) => void;
  onCreateTask?: () => void;
  compactMode?: boolean;
}> = ({ column, users, onSelectTask, onCreateTask, compactMode }) => {
  const { setNodeRef } = useDroppable({ id: column.id });

  return (
    <div
      ref={setNodeRef}
      className={`bg-gray-50 rounded-lg ${compactMode ? 'p-2 min-h-64 min-w-64' : 'p-3 min-h-80 min-w-72'} flex-1`}
    >
      {/* Column Header */}
      <div className={`flex items-center justify-between ${compactMode ? 'mb-2' : 'mb-3'}`}>
        <div className="flex items-center">
          <div className={`w-3 h-3 rounded-full ${column.color} mr-2`}></div>
          <h3 className="font-medium text-gray-900">
            {column.title}
          </h3>
          <span className="ml-2 bg-gray-200 text-gray-700 text-xs px-2 py-1 rounded-full">
            {column.tasks.length}
          </span>
        </div>

        {/* Add Task Button */}
        <button
          onClick={onCreateTask}
          className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-200 rounded"
          title="Add task"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Tasks List */}
      <SortableContext items={column.tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
        <div className={compactMode ? "space-y-1" : "space-y-1.5"}>
          {column.tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              users={users}
              onSelect={onSelectTask}
              compactMode={compactMode}
            />
          ))}
        </div>
      </SortableContext>
    </div>
  );
};

// Main Kanban Board Component
const KanbanBoard: React.FC<KanbanBoardProps> = ({
  tasks,
  users,
  onTaskUpdate,
  onSelectTask,
  onCreateTask,
  compactMode = false,
}) => {
  const [activeTask, setActiveTask] = useState<Task | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 3,
      },
    })
  );

  // Define columns
  const columns: Column[] = [
    {
      id: 'todo',
      title: 'To Do',
      status: 'todo',
      color: 'bg-gray-400',
      tasks: tasks.filter(task => task.status === 'todo'),
    },
    {
      id: 'in_progress',
      title: 'In Progress',
      status: 'in_progress',
      color: 'bg-blue-400',
      tasks: tasks.filter(task => task.status === 'in_progress'),
    },
    {
      id: 'review',
      title: 'Review',
      status: 'review',
      color: 'bg-yellow-400',
      tasks: tasks.filter(task => task.status === 'review'),
    },
    {
      id: 'completed',
      title: 'Completed',
      status: 'completed',
      color: 'bg-green-400',
      tasks: tasks.filter(task => task.status === 'completed'),
    },
  ];

  const handleDragStart = (event: DragStartEvent) => {
    const task = tasks.find(t => t.id === event.active.id);
    if (task) {
      setActiveTask(task);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    // Handle drag over logic if needed
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over) {
      setActiveTask(null);
      return;
    }

    const taskId = active.id as number;
    const task = tasks.find(t => t.id === taskId);

    if (!task) {
      setActiveTask(null);
      return;
    }

    // Determine the new status based on the drop target
    let newStatus = task.status;

    // Check if dropped on a column
    const targetColumn = columns.find(col => col.id === over.id);
    if (targetColumn) {
      newStatus = targetColumn.status as Task['status'];
    } else {
      // Check if dropped on another task (same column)
      const targetTask = tasks.find(t => t.id === over.id);
      if (targetTask) {
        newStatus = targetTask.status;
      }
    }

    // Update task status if it changed
    if (newStatus !== task.status) {
      try {
        await onTaskUpdate(taskId, { status: newStatus });
      } catch (error) {
        console.error('Failed to update task status:', error);
      }
    }

    setActiveTask(null);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className={`flex ${compactMode ? 'gap-3' : 'gap-4'} overflow-x-auto ${compactMode ? 'pb-3' : 'pb-4'}`}>
        {columns.map((column) => (
          <KanbanColumn
            key={column.id}
            column={column}
            users={users}
            onSelectTask={onSelectTask}
            onCreateTask={onCreateTask}
            compactMode={compactMode}
          />
        ))}
      </div>

      {/* Drag Overlay */}
      <DragOverlay>
        {activeTask ? (
          <TaskCard
            task={activeTask}
            users={users}
            compactMode={compactMode}
          />
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default KanbanBoard;